import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Coach, Group } from "../api/client";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";

const DEFAULT_COLOR = "#3B82F6";

const PASSWORD_TAKEN =
  "Этот пароль уже у другого тренера. Тренер входит в приложение только по паролю — придумайте другой.";

type FormState = {
  name: string;
  phone: string;
  email: string;
  birthDate: string;
  password: string;
  color: string;
  groupIDs: string[];
};

const EMPTY_FORM: FormState = {
  name: "",
  phone: "",
  email: "",
  birthDate: "",
  password: "",
  color: DEFAULT_COLOR,
  groupIDs: [],
};

const formOf = (coach: Coach): FormState => ({
  name: coach.name,
  phone: coach.phone ?? "",
  email: coach.email ?? "",
  birthDate: coach.birth_date ?? "",
  password: "",
  color: coach.color || DEFAULT_COLOR,
  groupIDs: (coach.groups ?? []).map((g) => g.id),
});

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

export default function CoachesPage() {
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingID, setEditingID] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const formRef = useRef<HTMLFormElement>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const [coachList, groupList] = await Promise.all([api.listCoaches(), api.listGroups()]);
      setCoaches((coachList ?? []).slice().sort(byName));
      setGroups((groupList ?? []).slice().sort(byName));
    } catch (err: any) {
      console.error("Failed to load coaches", err);
      setError(err?.message || "Не удалось загрузить тренеров");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const editing = editingID === null ? null : coaches.find((c) => c.id === editingID) ?? null;
  const name = form.name.trim();
  const password = form.password.trim();
  const canSubmit = !busy && name !== "" && (editing !== null || password !== "");

  const set = (changes: Partial<FormState>) => setForm((f) => ({ ...f, ...changes }));

  const toggleGroup = (id: string) =>
    setForm((f) => ({
      ...f,
      groupIDs: f.groupIDs.includes(id) ? f.groupIDs.filter((g) => g !== id) : [...f.groupIDs, id],
    }));

  const startEditing = (coach: Coach) => {
    setError(null);
    setEditingID(coach.id);
    setForm(formOf(coach));
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const stopEditing = () => {
    setEditingID(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      if (password && (await api.isCoachPasswordTaken(password))) {
        setError(PASSWORD_TAKEN);
        return;
      }
      const fields = {
        name,
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        birth_date: form.birthDate || null,
        color: form.color,
        group_ids: groups.filter((g) => form.groupIDs.includes(g.id)).map((g) => g.id),
      };
      if (editing) {
        await api.updateCoach(editing.id, password ? { ...fields, password } : fields);
      } else {
        await api.createCoach({ ...fields, password });
      }
      stopEditing();
      await load();
    } catch (err: any) {
      console.error("Coach save failed", err);
      setError(err?.message || (editing ? "Не удалось сохранить тренера" : "Не удалось добавить тренера"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (coach: Coach) => {
    const confirmed = await confirm({
      title: `Удалить тренера «${coach.name}»?`,
      body: (
        <>
          <p>
            Тренер больше не сможет войти в приложение. Вместе с ним удалятся его назначения на
            группы, награды тренера, история ставок и выплаты.
          </p>
          <p>Тренировки и тесты нормативов, которые он вёл, останутся, но без тренера.</p>
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить тренера",
      danger: true,
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteCoach(coach.id);
      if (editingID === coach.id) stopEditing();
    } catch (err: any) {
      console.error("Coach delete failed", err);
      setError(err?.message || "Не удалось удалить тренера");
    } finally {
      setBusy(false);
      await load();
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Тренеры</h2>
      <p className="page-description">
        Тренер входит в приложение только по паролю, поэтому пароль у каждого свой. Когда
        тренера назначают на группу, сервер сам добавляет его в командный чат этой группы.
        Ставка и выплаты — в отдельном разделе.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <form
        ref={formRef}
        className="mav-card cp-form coach-form"
        aria-label={editing ? "Изменение тренера" : "Новый тренер"}
        onSubmit={submit}
      >
        <h3 className="coach-form-title">{editing ? `Изменение: ${editing.name}` : "Новый тренер"}</h3>
        <label className="cp-field">
          <span>Имя</span>
          <input className="mav-input" value={form.name} maxLength={100} disabled={busy} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="cp-field">
          <span>Телефон</span>
          <input className="mav-input" type="tel" value={form.phone} maxLength={30} disabled={busy} onChange={(e) => set({ phone: e.target.value })} />
        </label>
        <label className="cp-field">
          <span>Почта</span>
          <input className="mav-input" type="email" value={form.email} maxLength={100} disabled={busy} onChange={(e) => set({ email: e.target.value })} />
        </label>
        <label className="cp-field">
          <span>Дата рождения</span>
          <input className="mav-input" type="date" value={form.birthDate} disabled={busy} onChange={(e) => set({ birthDate: e.target.value })} />
        </label>
        <div className="cp-field">
          <label className="cp-field">
            <span>{editing ? "Новый пароль" : "Пароль"}</span>
            <input
              className="mav-input"
              value={form.password}
              maxLength={100}
              autoComplete="off"
              disabled={busy}
              onChange={(e) => set({ password: e.target.value })}
            />
          </label>
          {editing && <span className="mav-hint">Оставьте пустым, чтобы не менять</span>}
        </div>
        <label className="cp-field">
          <span>Цвет</span>
          <input type="color" className="group-color-input" value={form.color} disabled={busy} onChange={(e) => set({ color: e.target.value })} />
        </label>
        <fieldset className="coach-groups" disabled={busy}>
          <legend>Группы</legend>
          {groups.length === 0 ? (
            <span className="mav-hint">Групп пока нет</span>
          ) : (
            <div className="coach-group-options">
              {groups.map((g) => (
                <label key={g.id} className="coach-group-option">
                  <input type="checkbox" checked={form.groupIDs.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                  <span>{g.name}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
        <div className="cp-actions mav-actions">
          <button type="submit" className="mav-btn-primary" disabled={!canSubmit}>
            {editing ? "Сохранить" : "Добавить тренера"}
          </button>
          {editing && (
            <button type="button" className="mav-btn-secondary" disabled={busy} onClick={stopEditing}>
              Отмена
            </button>
          )}
        </div>
      </form>

      <div className="table-wrap">
        <table className="settings-table stack-table coach-table">
          <thead>
            <tr>
              <th>Тренер</th>
              <th>Телефон</th>
              <th>Группы</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {coaches.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-cell">
                  {error ? "—" : "Тренеров пока нет"}
                </td>
              </tr>
            ) : (
              coaches.map((c) => (
                <tr key={c.id}>
                  <td className="cell-coach-name">
                    <span className="group-name">
                      <span className="group-swatch" style={{ background: c.color || DEFAULT_COLOR }} aria-hidden="true" />
                      {c.name}
                    </span>
                  </td>
                  <td className="cell-coach-phone">{c.phone || "—"}</td>
                  <td className="cell-coach-groups">
                    {(c.groups ?? []).length === 0 ? (
                      <span className="mav-status-muted">Без групп</span>
                    ) : (
                      <span className="coach-group-list">
                        {(c.groups ?? []).map((g) => (
                          <span key={g.id} className="mav-badge mav-badge-muted">
                            {g.name}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="cell-action">
                    <div className="row-actions">
                      <button className="mav-btn-secondary" disabled={busy} onClick={() => startEditing(c)}>
                        Изменить
                      </button>
                      <button className="btn-quiet-danger" disabled={busy} onClick={() => remove(c)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
